<template>
  <b-container fluid class="d-flex flex-column justify-content-between py-3">
    <b-row>
      <b-col cols="12" class="pb-3">
        <b-form-input
          v-model="userId"
          placeholder="ユーザーIDを入力します"
          ref="userIdInput"
          type="text"
          :disabled="disabledInput"
        ></b-form-input>
      </b-col>
      <b-col cols="12" class="pb-3">
        <b-form-input
          v-model="password"
          placeholder="パスワードを入力します"
          ref="passwordInput"
          type="password"
        ></b-form-input>
      </b-col>
      <b-col cols="12">
        <b-button block pill variant="primary" @click="onClickLogin">ログイン</b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <img src="@/assets/logo.png" fluid alt="ロゴ" class="w-100" />
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import axios from 'axios';
import { fileExistsTermInfo, fileGetTermInfo } from '@/lib/kjs-wrapper';
import { authUserAPI } from '@/api';
import { inputMode } from '@/constants';

export default {
  name: 'UserLogin',
  data() {
    return {
      userId: null, // ユーザーID
      password: null, // パスワード
      selectedInputMode: null // 選択中入力モード
    };
  },
  async mounted() {
    // 端末情報が存在しない場合は端末情報登録画面へ
    if (!fileExistsTermInfo()) {
      this.$router.replace({ path: '/RegisterTerminal' });
      return;
    }

    // 端末ID、端末
    const ret = await fileGetTermInfo();

    // WebAPI デフォルト情報セット
    const serverUrl = ret.serverUrl;
    const termId = ret.termId;
    let codeTypes = null;
    if (ret.codeTypes) codeTypes = ret.codeTypes.split('|');
    this.$emit('updateCodeTypes', codeTypes);
    axios.defaults.baseURL = serverUrl;
    axios.defaults.headers.post['Content-Type'] = 'application/json';

    // WebAPI カスタムヘッダセット
    axios.defaults.headers.common['X-FreeCheck-TermId'] = termId;
    axios.defaults.headers.common['X-FreeCheck-UserId'] = null;

    // 端末情報保持
    await this.$store.dispatch('setTerminalAction', termId);
    if (!this.$store.getters.term) {
      this.$store.commit('setMessage', '通信エラー(端末情報取得)');
      return;
    }

    // ログイン前初期値セット
    await this.$store.dispatch('setLogoutStateAction');

    // スキャンコードが空白の場合は出力履歴画面へ
    if (!codeTypes) {
      this.$router.replace({ path: '/ExportHistory' });
      return;
    }

    // 画面を初期化
    console.log(this.$options);
    this.$emit('initView', {
      viewId: this.$options.name, // 画面ID
      useToolButtons: true, // ツールボタン使用
      defaultInputMode: inputMode.KEY, // デフォルト手入力
      usableInputModeBits: inputMode.BARCODE + inputMode.KEY // バーコード＋手入力
    });

    // 自動ログイン
    const term = this.$store.getters.term;
    if (term?.autoLoginUserId) {
      const ret = await this.login(term.autoLoginUserId, null);
      if (ret) {
        // 照合実績画面へ
        this.$router.replace({ path: `/${term.defaultFormId}` });
        return;
      }
    }

    this.$emit('enabledScan', true);

    // ユーザIDにフォーカスをセット
    this.$nextTick(() => this.$refs.userIdInput.focus());
  },
  computed: {
    disabledInput: function () {
      return this.selectedInputMode !== inputMode.KEY;
    }
  },
  methods: {
    /**
     * 入力モード変更処理(親コンポーネントより)
     * @param {string} mode 入力モード(1:手入力、2:OCR、4:バーコード)
     */
    changeInputMode: function (mode) {
      this.selectedInputMode = mode;
      if (mode !== inputMode.KEY) {
        if (this.userId) {
          this.$store.commit('setMessage', null);
          this.$refs.passwordInput.focus();
        } else this.$store.commit('setMessage', 'ユーザーIDを読取します');
      } else {
        this.$store.commit('setMessage', null);
        if (this.userId) this.$refs.passwordInput.focus();
        else this.$refs.userIdInput.focus();
      }
    },
    /**
     * 入力クリア(親コンポーネントより)
     */
    clearInput: function () {
      this.userId = null;
      this.password = null;
    },
    /**
     * スキャンデータ解析後(親コンポーネントより)
     * @param {object} headerValue ヘッダ値
     * @param {array} detailValues 明細値配列
     * @param {object} barcode 対象バーコード
     * @param {string} dupType 重複区分(1:重複OK、2:重複NG)
     * @returns {boolean} スキャンデータ反映成否
     */
    analyzedScanData: function (headerValue, detailValues, barcode, dupType) {
      if ('USER_ID' in headerValue) this.userId = headerValue['USER_ID'];
      if ('PASSWORD' in headerValue) this.password = headerValue['PASSWORD'];

      // ログイン処理を行う
      this.onClickLogin();

      return true;
    },
    /**
     * ログインボタン
     */
    onClickLogin: async function () {
      this.$store.commit('setMessage', null);

      // 入力チェック
      if (!this.userId || !this.password) {
        this.changeInputMode(this.selectedInputMode);
        return;
      }

      // ユーザ認証
      const ret = await this.login(this.userId, this.password);
      if (!ret) {
        this.$store.commit('setMessage', '認証に失敗しました');
        return;
      }

      // 既定画面(未設定:照合実績画面)へ
      const term = this.$store.getters.term;
      this.$router.replace(`/${this.$store.getters.term['defaultFormId'] ?? 'VersionInfo'}`);
    },
    /**
     * ログイン処理
     * @param {string} userId ユーザID
     * @param {string} password パスワード
     */
    login: async function (userId, password) {
      // ユーザ認証APIへ
      const ret = await authUserAPI(userId, password);
      if (!ret) return null;

      // APIリクエストヘッダに担当者IDをセット
      axios.defaults.headers.common['X-FreeCheck-UserId'] = ret.data.userId;

      // ログイン初期値セット
      await this.$store.dispatch('setLoginStateAction', ret.data);

      return ret.data;
    }
  }
};
</script>

<style>
div.container-fluid {
  height: 100%;
}
</style>
